'use client'
import * as React from 'react'
import { ChevronLeft, ChevronRight, Quote, Star, ExternalLink } from 'lucide-react'
import { customerTestimonials, type CustomerTestimonial } from '@/lib/copy/customer-testimonials'

// Tolstoy-style customer success carousel, Idukki-flavoured. Each
// slide pairs a big brand wordmark + source attribution on the left
// with a pull-quote, reviewer credit and 3 stat tiles on the right.
// Real testimonials only, pulls verbatim from customerTestimonials,
// which itself sources directly from Shopify App Store + G2.

const SOURCE_LABEL: Record<CustomerTestimonial['source'], string> = {
  shopify: 'Shopify App Store',
  g2:      'G2',
  idukki:  'idukki.io',
}

const SOURCE_HREF: Record<CustomerTestimonial['source'], string> = {
  shopify: 'https://apps.shopify.com/idukki-no-code-ugc-platform',
  g2:      'https://www.g2.com/products/idukki/reviews',
  idukki:  'https://idukki.io',
}

export function CustomerSuccessCarousel({
  items,
  className,
}: {
  items?: CustomerTestimonial[]
  className?: string
}) {
  const list = items && items.length > 0 ? items : customerTestimonials.slice(0, 6)
  const [i, setI] = React.useState(0)
  const t = list[i]

  const prev = () => setI((n) => (n - 1 + list.length) % list.length)
  const next = () => setI((n) => (n + 1) % list.length)

  return (
    <section className={`container-wide py-16 md:py-24 ${className || ''}`}>
      <div className="mx-auto max-w-3xl text-center md:text-left">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-primary">
          Trusted by the best
        </p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-balance md:text-4xl">
          What real Idukki customers <span className="display-italic">actually say</span>.
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Every quote below is verbatim from a real customer. Shopify App Store and G2 reviews link to the original; direct customer quotes are credited by name and role.
        </p>
      </div>

      <div className="relative mt-10">
        <div className="grid items-stretch gap-8 rounded-3xl border bg-card p-6 shadow-sm md:grid-cols-[1fr_1.4fr] md:gap-12 md:p-10">
          {/* LEFT, brand wordmark + source */}
          <div className="flex flex-col justify-between">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                {SOURCE_LABEL[t.source]} · {t.date}
              </p>
              <p className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
                {t.brand}
              </p>
              {t.region && (
                <p className="mt-2 text-sm text-muted-foreground">{t.region}{t.duration ? ` · ${t.duration}` : ''}</p>
              )}
              {t.rating !== undefined && (
                <div className="mt-4 inline-flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, n) => (
                    <Star
                      key={n}
                      className={`h-4 w-4 ${n < t.rating! ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
                    />
                  ))}
                  <span className="ml-1 text-xs font-semibold">{t.rating!.toFixed(1)}</span>
                </div>
              )}
            </div>
            {/* Only Shopify + G2 entries have a public review to link back to.
                'idukki'-sourced quotes are direct customer correspondence with
                no independent review page, so there's nothing to verify against
                and no link is shown for them. */}
            {t.source !== 'idukki' && (
              <a
                href={SOURCE_HREF[t.source]}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Read the full review on {SOURCE_LABEL[t.source]} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* RIGHT, pull-quote + reviewer + 3 stats */}
          <div className="flex flex-col">
            <Quote className="h-6 w-6 text-primary" />
            <p className="mt-4 text-lg leading-relaxed text-foreground md:text-xl">
              {t.quote}
            </p>
            {/* t.pull is a short excerpt of the same quote, meant for compact
                contexts that don't have room for the full quote (see its
                other uses in testimonials/page.tsx, pricing/page.tsx). This
                carousel already shows the full quote above, so rendering
                pull here just repeats the same sentence back to the reader. */}
            {(t.reviewer || t.role || t.title) && (
              <div className="mt-6 border-t pt-5">
                {t.title && <p className="font-display text-base font-semibold leading-tight">{t.title}</p>}
                {t.reviewer && <p className="mt-1 text-sm font-medium">{t.reviewer}</p>}
                {t.role && <p className="text-xs text-muted-foreground">{t.role}</p>}
              </div>
            )}
            {/* Stat tiles, pulled from whatever the testimonial has.
                Rating, duration, source. Doesn't fabricate numbers. */}
            <div className="mt-6 grid grid-cols-3 gap-3 border-t pt-5">
              <div>
                <p className="font-display text-2xl font-semibold tracking-tight">
                  {t.rating !== undefined ? `${t.rating.toFixed(1)}★` : '5.0★'}
                </p>
                <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Rating</p>
              </div>
              <div>
                <p className="font-display text-2xl font-semibold tracking-tight truncate">
                  {t.duration?.replace(' using the app', '') || 'Verified'}
                </p>
                <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Tenure</p>
              </div>
              <div>
                <p className="font-display text-2xl font-semibold tracking-tight">
                  {SOURCE_LABEL[t.source].split(' ')[0]}
                </p>
                <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Source</p>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel controls */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            aria-label="Previous"
            onClick={prev}
            className="flex h-9 w-9 items-center justify-center rounded-full border bg-card transition hover:border-primary/40 hover:bg-secondary"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {list.map((_, n) => (
              <button
                key={n}
                type="button"
                aria-label={`Go to review ${n + 1}`}
                onClick={() => setI(n)}
                className={`h-1.5 rounded-full transition ${n === i ? 'w-5 bg-primary' : 'w-1.5 bg-foreground/20'}`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="Next"
            onClick={next}
            className="flex h-9 w-9 items-center justify-center rounded-full border bg-card transition hover:border-primary/40 hover:bg-secondary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          {i + 1} of {list.length}
        </p>
      </div>
    </section>
  )
}
